/**
 * core
 *
 * This is the "core" of our application (in "Scalable JavaScript Application
 * Architecture" parlance; see
 * http://code.tutsplus.com/tutorials/writing-modular-javascript--net-14746).
 *
 * @author    Richard J. Turner <richard.turner@mediatel.co.uk>
 * @copyright 2015 Mediatel Limited.
 */

"use strict";

const moduleData = {};

let log = console;

/**
 * @param {function} $
 * @param {function} sandboxFactory
 * @return {Core}
 */
export default ($, sandboxFactory) => (
/**
 * @typedef {object} Core
 * @property {function} setLogger
 * @property {function} log
 * @property {function} createModule
 * @property {function} start
 * @property {function} startAll
 * @property {function} stop
 * @property {function} stopAll
 * @property {function} registerEvents
 * @property {function} triggerEvent
 * @property {function} removeEvents
 * @property {function} get
 * @property {function} post
 * @property {Object} dom
 */
{
    setLogger(logger) {
        log = logger;
    },

    /**
     * @param {string} severity One of "log", "warn", "error".
     * @param {string} message The error message to log.
     */
    log(severity, message) {
        log[severity](message);
    },

    /**
     * @param {string} moduleId The ID for the module to create.
     * @param {function} factory The factory function for creating the module.
     */
    createModule(moduleId, factory) {
        if (typeof moduleId !== "string" || typeof factory !== "function") {
            this.log(
                "error",
                `Module '${moduleId}' registration failed: one or more arguments have the wrong type.`
            );
            return;
        }

        const temp = factory(sandboxFactory(this, moduleId));

        if (typeof temp.init !== "function" || typeof temp.destroy !== "function") {
            this.log(
                "error",
                `Module '${moduleId}' registration failed: module did not have init() and destroy() functions.`
            );
            return;
        }

        moduleData[moduleId] = { factory, instance: null };
    },

    /**
     * @param {string} moduleId The ID of the module to start.
     */
    start(moduleId) {
        const module = moduleData[moduleId];
        if (module) {
            module.instance = module.factory(sandboxFactory(this, moduleId));
            module.instance.init();

            return;
        }

        this.log(
            "error",
            `Start module '${moduleId}' failed: it does not exist`
        );
    },

    startAll() {
        Object.keys(moduleData).forEach(moduleId => this.start(moduleId));
    },

    /**
     * @param {string} moduleId The ID of the module to stop.
     */
    stop(moduleId) {
        const module = moduleData[moduleId];

        if (module && module.instance) {
            module.instance.destroy();
            module.instance = null;

            return;
        }

        this.log(
            "error",
            `Stop module '${moduleId}' failed: it does not exist or had not been started`
        );
    },

    stopAll() {
        Object.keys(moduleData).forEach(moduleId => this.stop(moduleId));
    },

    /**
     * @param {object} events   A hash of event names and their handlers.
     * @param {string} moduleId The ID of the module to register these events for.
     */
    registerEvents(events, moduleId) {
        if (moduleData[moduleId]) {
            moduleData[moduleId].events = events;
        } else {
            this.log("error", `Module '${moduleId}' does not exist.`);
        }
    },

    /**
     * @param {object} event
     */
    triggerEvent(event) {
        Object.keys(moduleData).forEach(k => {
            let module = moduleData[k];

            if (module.events && module.events[event.type]) {
                module.events[event.type](event.data, event.target);
            }
        });
    },

    /**
     * @param {string[]} events
     * @param {string} moduleId
     */
    removeEvents(events, moduleId) {
        if (moduleData[moduleId] && moduleData[moduleId].events) {
            events.forEach(event => delete moduleData[moduleId].events[event]);
        }
    },

    /**
     * Sends an XHR GET request
     * @param {string} url
     */
    get(url) {
        return new Promise((resolve, reject) => {
            //noinspection JSUnresolvedFunction
            $.get(url).then(response => resolve(response)).fail(
                (...args) => reject(args)
            );
        });
    },

    /**
     * Sends an XHR POST request
     * @param {string} url
     * @param {object} data
     */
    post(url, data) {
        return new Promise((resolve, reject) => {
            //noinspection JSUnresolvedFunction
            $.post(url, data).then(response => resolve(response)).fail(
                (...args) => reject(args)
            );
        });
    },

    dom: {
        query(selector, context) {
            let $elems = (context && context.find)
                ? context.find(selector)
                : $(selector);

            const toReturn = $elems.get();
            /* We'll augment our result with some helper functions, giving it a
             similar API to jQuery. Note that this code assumes we are using
             jQuery as our helper library ($ and $elems). If not then we may see
             some properties/functions of $elems undefined. */
            toReturn.length = $elems.length;
            toReturn.query = sel => this.query(sel, $elems);
            toReturn.html = $elems.html;
            toReturn.scrollTop = $elems.scrollTop;
            toReturn.offset = $elems.offset;
            toReturn.css = $elems.css;
            toReturn.removeClass = $elems.removeClass;
            toReturn.addClass = $elems.addClass;
            toReturn.attr = $elems.attr;
            toReturn.height = $elems.height;
            toReturn.scrollHeight = $elems.scrollHeight;

            return toReturn;
        },

        create(html) {
            const $elems = $(html);
            const toReturn = $(html).get();

            toReturn.length = $elems.length;
            toReturn.query = sel => this.query(sel, $elems);
            toReturn.html = $elems.html;
            toReturn.scrollTop = $elems.scrollTop;
            toReturn.offset = $elems.offset;
            toReturn.css = $elems.css;
            toReturn.removeClass = $elems.removeClass;
            toReturn.addClass = $elems.addClass;
            toReturn.attr = $elems.attr;
            toReturn.height = $elems.height;
            toReturn.scrollHeight = $elems.scrollHeight;

            return toReturn;
        },

        ancestor(element, selector) {
            let $elem = $(element).closest(selector);
            let toReturn = $elem.get();

            toReturn.length = $elem.length;
            toReturn.query = sel => this.query(sel, $elem);
            toReturn.html = $elem.html;

            return toReturn;
        },

        /**
         * @param {HTMLElement} element
         */
        empty(element) {
            $(element).empty();
        },

        /**
         * @param {HTMLElement} element
         * @param {string|HTMLElement} child
         */
        append(element, child) {
            $(element).append(child);
        },

        /**
         * @param {HTMLElement} element
         */
        show(element) {
            $(element).show();
        },

        /**
         * @param {HTMLElement} element
         */
        hide(element) {
            $(element).hide();
        },

        bind(element, event, fn) {
            if (!element || !event || !fn) {
                log.error("An element, event and handler must be specified.");
                log.error(arguments);
                return;
            }

            $(element).on(event, fn);
        },

        unbind(element, event, fn) {
            if (!element || !event || !fn) {
                log.error("An element, event and handler must be specified.");
                return;
            }

            $(element).off(event, fn);
        },

        /**
         * Generates a chart and set it as the contents of the element
         * @param {HTMLElement} element
         * @param {object} options Chart options
         */
        chart(element, options) {
            $(element).highcharts(options);
        },

        datePicker(element, options) {
            $(element).datepicker(options);
        }
    }
});
